import { NestedLocation } from './nestedLocations';
import { RejectReason } from './taskForm';

export type SignatureCountry =
  | 'CL'
  | 'PE'
  | 'MX'
  | 'CO'
  | 'AR'
  | 'PY'
  | 'ES'
  | 'BR'
  | 'UY'
  | 'BO'
  | 'EC'
  | 'VE'
  | 'US'
  | 'CA'
  | 'DO'
  | 'CR'
  | 'PA'
  | 'GT'
  | 'SV'
  | 'HN'
  | 'NI'
  | 'PR'
  | 'CU'
  | 'JM'
  | 'PT'
  | 'FR'
  | 'DE'
  | 'IT'
  | 'GB'
  | 'IE'
  | 'NL'
  | 'BE'
  | 'CH'
  | 'AT'
  | 'SE'
  | 'NO'
  | 'DK'
  | 'FI'
  | 'IS'
  | 'PL'
  | 'CZ'
  | 'SK'
  | 'HU'
  | 'RO'
  | 'BG'
  | 'GR'
  | 'HR'
  | 'SI'
  | 'RS'
  | 'BA'
  | 'ME'
  | 'MK'
  | 'MT'
  | 'CY'
  | 'LU'
  | 'LT'
  | 'LV'
  | 'EE'
  | 'TR'
  | 'IL'
  | 'SA'
  | 'AE'
  | 'QA'
  | 'KW'
  | 'BH'
  | 'OM'
  | 'JO'
  | 'LB'
  | 'EG'
  | 'MA'
  | 'TN'
  | 'DZ'
  | 'ZA'
  | 'NG'
  | 'KE'
  | 'GH'
  | 'UG'
  | 'TZ'
  | 'RW'
  | 'BW'
  | 'NA'
  | 'ZM'
  | 'ZW'
  | 'ET'
  | 'CN'
  | 'IN'
  | 'PK'
  | 'BD'
  | 'JP'
  | 'KR'
  | 'SG'
  | 'MY'
  | 'ID'
  | 'PH'
  | 'TH'
  | 'VN'
  | 'HK'
  | 'TW'
  | 'LK'
  | 'NP'
  | 'AU'
  | 'NZ'
  | 'RU'
  | 'UA'
  | 'BY'
  | 'KZ'
  | 'UZ'
  | 'GE'
  | 'AM'
  | 'AZ';

export interface SignatureCountryFlag {
  name: string;
  flag: string;
}

export type IDNames = { personIdName: string; companyIdName: string; code: SignatureCountry };

export interface SignatureBody {
  signature_config: {
    company_rut: string;
    company_name: string;
    company_address: string;
    company_representative: string;
    company_representative_rut: string;
  };
}

export interface SignatureResponse {
  active: boolean;
  id: number;
  company_name: string;
  rut: string;
  legal_representative: string;
  legal_representative_rut: string;
  legal_address: string;
}

export interface NationalIdNumber {
  value: string;
  valid: boolean;
  country: SignatureCountry;
}

export interface TaxIdCompany {
  value: string;
  valid: boolean;
  country: SignatureCountry;
}

export interface TaxIdUser {
  value: string;
  valid: boolean;
  country: SignatureCountry;
}

export interface UserSignatureState {
  is_user_config_ok: boolean;
  is_account_config_ok: boolean;
  is_account_config_current_verified: boolean;
  is_user_config_current_verified: boolean;
  did_user_verify_signature: boolean;
}

export interface FormAnswerSignatureState {
  form_answer_has_request: boolean;
  is_user_in_request: boolean;
  has_signed: boolean;
  all_users_signed: boolean;
  is_author: boolean;
}

export enum SignatureStatusMap {
  PENDING = 0,
  COMPLETED = 1,
  REJECTED = 2,
}

export type SignatureStatus = 'rejected' | 'completed' | 'pending' | string;

export interface SignatureRequest {
  answer_id: number;
  form_code: string;
  form_title: string;
  request_id: number;
  requested_by: string;
  updated_at: string;
  pending_users: { id: number; name: string }[];
  signed_by: { id: number; name: string }[];
  requested_at: string;
  form_id: number;
  status: SignatureStatus;
  /** Firmas necesarias para completar (quórum si aplica, total si no) */
  required_signatures?: number;
  /** Solo en filas rechazadas (DS-3811). */
  reject_reason?: string | null;
  rejected_by?: string | null;
  rejected_at?: string | null;
  last_activity?: SignatureLastActivity | null;
  /** The request's own provenance location, via its form_answer's task_assign --
   *  null for a request whose answer has no task_assign. */
  location_id: number | null;
  location_type: 'Location' | 'NestableLocation' | null;
  location_name: string | null;
  /** null for a legacy Location (no place type concept), or when
   *  location_type isn't NestableLocation at all. */
  location_place_type: NestedLocation['place_type_id'] | null;
}

export interface SignatureItem {
  mandatory: boolean;
  answer_id: number;
  form_code: string;
  form_title: string;
  request_id: number;
  requested_by: string;
  updated_at: string;
  user_form_signature_request_id: number;
  requested_at: string;
  form_id: number;
  /** State of the whole request, across every signer. */
  status?: SignatureStatus;
  /**
   * DS-3672. This signer's own state, which is what "Mis firmas" and its status
   * cards are about. Differs from `status` when the request is still open because
   * another signer has not acted yet: `status` is pending, `user_status` completed.
   */
  user_status?: SignatureStatus;
  /** Solo en filas rechazadas (DS-3811). */
  reject_reason?: string | null;
  rejected_by?: string | null;
  rejected_at?: string | null;
  last_activity?: SignatureLastActivity | null;
  /** The request's own provenance location, via its form_answer's task_assign --
   *  null for a request whose answer has no task_assign. */
  location_id: number | null;
  location_type: 'Location' | 'NestableLocation' | null;
  location_name: string | null;
  /** null for a legacy Location (no place type concept), or when
   *  location_type isn't NestableLocation at all. */
  location_place_type: NestedLocation['place_type_id'] | null;
}

export interface SignatureIndex {
  form_signature_requests: SignatureItem[];
  total_pages: number;
  current_page: number;
  total_requests: number;
}

export type PdfLink = { link: string };

export type SignatureType = 'pending' | 'completed';

// DS-3672. With SignatureRejectionUX on, rejected is a bucket of its own instead
// of being folded into completed, so the tab set grows by one.
export type SignatureTabType = SignatureType | 'rejected';

// DS-3672/DS-3811. The filters above both tables. Everything is optional: the
// backend treats absent params as "no filter", so the app and other callers are
// unaffected. The 30-day default lives here in the UI, not in the query.
export interface SignatureListFilters {
  date_from?: string;
  date_to?: string;
  // DS-3672. Which moment the range is read against. Absent means the sent date, so a
  // caller that does not know about it filters exactly as before. `signed` means this
  // signer's own signature in "Mis firmas" and the request's finalisation in
  // "Solicitudes enviadas".
  date_field?: 'signed';
  form_ids?: number[];
  form_code?: string;
  requester_ids?: number[];
  participant_ids?: number[];
  // DS-3672. Status is a filter over one unified table, not a tab that swaps
  // between three of them. Absent means every status.
  statuses?: SignatureTabType[];
  // DS-4046. One or more NestableLocation ids; the backend expands each to its
  // full branch (DS-4038). No leaf/branch distinction on this side -- Firmas has
  // no flat mode to preserve. Absent means no location filter, never an empty list.
  location_with_descendants_id?: number[];
  // DS-4043's AC-28 (query-param override on Mis firmas) reuses this same shape:
  // a single id becomes a one-element array, not a separate number field.
}

export interface SignatureIndexParams extends SignatureListFilters {
  /**
   * Legacy single-status param, sent only while SignatureRejectionUX is off so
   * the rollback path queries the backend exactly as it does today. With the flag
   * on, the status set travels in `statuses`.
   */
  status?: SignatureTabType;
  search?: string;
  page?: number;
  per_page?: number;
}

// One event of the "última actividad" column: what happened last to a request.
export interface SignatureLastActivity {
  // El `| string` deja el tipo en string y no documenta nada, pero los literales sí:
  // dicen qué emite el backend hoy. `corrected` es una rechazada que ya fue corregida,
  // que gana sobre el rechazo y manda la fila a Finalizadas (DS-3811).
  action: 'signed' | 'rejected' | 'corrected' | string;
  date: string;
  user?: string | null;
  reject_reason?: string | null;
}

// Counts behind the three status cards. Keys match the backend buckets; the two
// tabs label them differently (Pendientes/Firmadas/Rechazadas in "Mis firmas",
// En curso/Finalizadas/Rechazadas in "Solicitudes enviadas").
export interface SignatureStatusCounts {
  pending: number;
  completed: number;
  rejected: number;
  /** Every row of the tab, for the count next to its label. Not the sum above. */
  total?: number;
}

export interface SignatureRequestResponse {
  form_signature_requests: SignatureRequest[];
  total_requests: number;
}

export interface UserFormSignatureInfo {
  user_has_signed: boolean;
  user_can_sign: boolean;
  user_signature_verified: boolean;
  account_config_current?: boolean;
  form_should_be_signed_by_user: boolean;
  form_signature_request_required: boolean;
  user_form_signature_request: {
    id: number;
  };
  has_request: boolean;
  user_has_rejected?: boolean;
  /**
   * The request is rejected, whoever rejected it. Distinct from user_has_rejected,
   * which is "I rejected it": correcting is asked of whoever answered the form or
   * whoever manages the request, rarely of whoever rejected.
   */
  request_rejected?: boolean;
  cancelled_by_quorum?: boolean;
  reject_reasons?: RejectReason[];
  /**
   * Whether correcting puts the request back in flight on its own. False for a request
   * whose signers were picked by hand: regenerating on edit only ever existed for forms
   * with automatic signature, so there the correction saves and nothing is re-sent.
   */
  correction_resends_request?: boolean;
  /**
   * The request in flight is the one a correction created, so there is nothing left to
   * correct. Distinct from `has_request` alone, which is also true for a request nobody
   * has corrected.
   */
  request_already_corrected?: boolean;
}

export interface ExternalUserFormSignatureInfo {
  full_name: string;
  rut: string;
  company_name: string;
  role: string;
  has_signed: boolean;
  has_rejected: boolean;
  should_user_sign: boolean;
  cancelled_by_quorum?: boolean;
  account_country: SignatureCountry;
  form_signature_request_required: boolean;
  user_form_signature_request: {
    id: number;
  };
  public_pdf_url: string;
  reject_reasons?: RejectReason[];
  // Standardized-reject flag for the account, sent in the payload because the
  // anonymous/token viewer cannot read feature flags client-side (DS-3659).
  standardized_reject_reasons_enabled?: boolean;
  // Rejected-signer info for the red notice, sent here because the completed
  // signatures endpoint is authenticated and 401s for a token viewer (DS-3659).
  rejected_signatures?: SignerInfo[];
}

export interface UpdateExternalUserSignatureInfoBody {
  full_name: string;
  rut: string;
  company_name: string;
  role: string;
}

export interface SignatureRequestTimelineEvent {
  action:
    | 'created-required'
    | 'created-optional'
    | 'signed'
    | 'rejected'
    | 'signature-requested'
    // DS-3673: emitted by the merged reject -> correct timeline.
    | 'corrected'
    | 'completed';
  date: string;
  user?: string | string[];
  order?: {
    type: 'simultaneous' | 'sequential';
    users: string[] | string[][];
    minimum_signatures?: number;
    minimum_signatures_per_step?: Record<string, number>;
  };
  signer_order?: string;
  user_order?: string;
  group_total?: number;
  min_required?: number;
  signed_count?: number;
  reject_reason?: string;
}

export interface SignatureRequestTimelineResponse {
  timeline: SignatureRequestTimelineEvent[];
}

export interface SignerInfo {
  name: string;
  rut: string;
  email: string;
  date: string;
  country: SignatureCountry;
  rejected?: boolean;
  company_name?: string;
  reject_reason?: string;
}
